import { TFunction } from 'i18next'
import { MouseEvent, ReactNode } from 'react'

import { Nullable } from '@types'

import { SelectOption } from '@components/SelectWithLabeledValue/index.types'
import { ProfileAlert } from '@modules/alerts/types'
import { CompanyContactOverview } from '@modules/contacts/types'
import { AnyProfile, AnyResultProfile, AssetLatestCompany, HistoryProfileEvent, ProfileType } from '@modules/search/types'
import { Header } from '@tanstack/react-table'

export enum CustomColumnType {
  FREE_TEXT = 'text',
  AUTOCOMPLETE = 'autocomplete',
}

export type ShortlistItemProfile = {
  id: number
  profileType: ProfileType
  searchDocId: number | string
  note?: string
  nda?: boolean
  priority?: ShortlistProfilePriority
  tagId?: number | null
  customFieldValues?: {
    [key: number]: string
  }
  createdAt: string
  alertUpdatedAt: string
}

export interface ShortlistProfileData extends ShortlistItemProfile {
  id: number
  shortlistId: number
  hasSharedInfo: boolean
  status: string
  profileMetadata: AnyResultProfile
  hasContacted?: boolean
  isContactable?: boolean
  companyId: number
  companyName: string
  createdAt: string
}

export interface ShortListCustomFieldOption {
  id: string
  value: string
}

export interface ShortListCustomField {
  id: number
  fieldName: string
  fieldType: CustomColumnType
  createdAt?: string
  fieldOptions?: ShortListCustomFieldOption[]
  updatedAt?: string
}

export type Shortlist = {
  id: number
  p1UserId: number
  name: string
  status: number
  updatedAt: string
  createdAt: string
  profiles: ShortlistItemProfile[]
  columns: Nullable<ShortlistCategoriesConfigProps>
  type?: 'default' | 'alert'
  customFields?: ShortListCustomField[]
  filters?: ApiFilterItem<ShortlistFilterField>[]
}

export interface ShortlistProfile extends ShortlistProfileData {
  updatedAt: string
  createdAt: string
  deletedAt: string
}

export interface ShortlistProfileDataResponse {
  result: ShortlistProfileData[]
  total: number
}

/** @TODO we don't need this type, we must not modify profiles to look differently */
export interface ShortlistExtendedProfile {
  profileId: Shortlist['id']
  globalId: AnyProfile['id']
  profileType: AnyProfile['@type']
  profileStatus: string
  profileTitle: string
  profileAlertUpdatedAt: number
  profileHasSharedInfo?: boolean
  profileImgData: AnyProfile
  profileHasPublicEvent: boolean
  profileEvents?: HistoryProfileEvent[]
  profileEventName: string
  profileEventStartDate: string
  profileEventEndDate: string
  profileUpdatedDate: string
  profileCreatedDate: string
  profileLatestCompany?: AssetLatestCompany
  profileDevPhase?: string
  companyId: number
  companyName: string
  note: Nullable<string>
  nda: Nullable<boolean>
  priority?: ShortlistProfilePriority
  hasContacted?: boolean
  tagId?: number
  isContactable: boolean
  customFieldValues?: {
    [key: number]: string
  }
  createdAt: string
  alertUpdatedAt?: string
}

export interface ShortlistPageDataProps {
  shortlistId?: number
  isEmptyShortlist: boolean
  size?: number
  from?: number
  sort?: ShortlistSort
  companyIds?: number[]
  customFieldSortId?: number
  searchQuery?: string
  allowNotSortable?: boolean
  filters: ShortlistColumnFilter[]
}

/** @TODO below types must not be there, refactor please and don't do like that anymore */
export interface ShortlistTableDataProps {
  totalProfilesAmount?: number
  profilesPageData: ShortlistExtendedProfile[]
  isPageDataLoading: boolean
}
export interface ShortlistTableProps extends ShortlistTableDataProps {
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  selectedRowIds: (string | number)[]
  onSelectProfile: (id: number | number[]) => void
  onClickProfile: () => void
  onEditNote: (event: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; note: string; profileType: string }) => void
  onEditCustomColumn: (
    event: Nullable<MouseEvent<HTMLElement>>,
    data?: { profileId: number; fieldId: number; customFieldValue: string },
  ) => void
  onChangePriority: (profileId: number, priority: ShortlistProfilePriority, profileType?: string) => void
  onChangeNda: (profileId: number, nda: boolean) => void
  onTagUpdate: (profileId: number, tagId?: number | null, profileType?: string) => void
  sort: ShortlistSort
  onSortChange: (sort: ShortlistSort) => void
  alertsShortlistId?: number
  activeShortlistId: number
  activeShortlist?: Shortlist
  handleEditCustomColumnSuccess: (customField: ShortListCustomField, closePopup: boolean) => void
  handleCloseEditCustomColumnPopover?: () => void
  onEditCustomColumnButtonClick?: (event: Nullable<React.MouseEvent<HTMLElement>>, id: number) => void
  updateConfig: (config: ShortlistCategoriesConfigProps) => void
}

export interface ShortlistTableHeadProps {
  optionalColumnsConfig: ShortlistTableProps['optionalColumnsConfig']
  allProfilesSelected: boolean
  onSelectAllProfiles: (allSelected: boolean) => void
  sort: ShortlistSort
  onSortChange: (sort: ShortlistSort) => void
  profiles: ShortlistExtendedProfile[]
  tableColumns: Header<ShortlistTableConfigProps, unknown>[]
  activeShortlistId: number
  activeShortlist?: Shortlist
  handleEditCustomColumnSuccess: (customField: ShortListCustomField, closePopup: boolean) => void
  handleCloseEditCustomColumnPopover?: () => void
  onEditCustomColumnButtonClick?: (event: Nullable<React.MouseEvent<HTMLElement>>, id: number) => void
  editCustomColumnPopoverAnchor?: Nullable<HTMLElement>
  updateConfig: (config: ShortlistCategoriesConfigProps) => void
}

export interface ShortlistSort {
  fieldName?: string
  customFieldId?: number
  sortOrder?: ShortlistSortOrder
}

export interface ShortlistTableRowProps {
  contactsOverview: Nullable<CompanyContactOverview>
  selectedRowIds: (string | number)[]
  profileData: ShortlistExtendedProfile
  optionalColumnsConfig?: ShortlistTableProps['optionalColumnsConfig']
  onSelectProfile: (value: number | number[]) => void
  onClickProfile: () => void
  onNoteClick: (event: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; note: string }) => void
  onCustomColumnClick: (
    event: Nullable<MouseEvent<HTMLElement>>,
    data?: { profileId: number; fieldId: number; customFieldValue: string },
  ) => void
  onContactClick: (e: MouseEvent<HTMLElement>, profile: ShortlistExtendedProfile) => void
  onContactStatusClick: (ref: Nullable<HTMLElement>, companyId?: number) => void
  onChangePriority: (profileId: number, priority: ShortlistProfilePriority) => void
  onChangeNda: (profileId: number, nda: boolean) => void
  onTagUpdate: (profileId: number, tagId?: number | null) => void
  alertsShortlistId?: number
  profileAlert?: ProfileAlert
  tableColumns: ShortlistTableConfigProps[]
  hasAccessToAlerts?: boolean
  priorityOptions: SelectOption[]
  attendingEvents: number[]
  t: TFunction
  hasAccessToChat: boolean
  isEcommerce?: boolean
  activeShortlist?: Shortlist
  isContactButtonEnabled: (companyId: number | null) => boolean
}

export interface ShortlistTableConfigProps {
  fieldName: string
  columnTitle?: string
  isEnabled?: boolean
  noSortable?: boolean
  restCellProps?: {
    $cellWidth?: number
    $isNameCell?: boolean
    $nowrap?: boolean
    $fixedWidth?: boolean
    $isContactCell?: boolean
    $isCentered?: boolean
    $actionable?: boolean
    $noPadding?: boolean
    $fitContent?: boolean
  }
  isDragHandle?: boolean
  isSortable?: boolean
  customDisable?: boolean
}

export interface ShortlistCategoriesPopoverProps {
  ndaFieldInitialValue: { visible: boolean; width: number }
  noteFieldInitialValue: { visible: boolean; width: number }
  tagIdFieldInitialValue: { visible: boolean; width: number }
  priorityInitialValue: { visible: boolean; width: number }
  dateAddedFieldInitialValue: { visible: boolean; width: number }
  companyFieldInitialValue: { visible: boolean; width: number }
  lastEventFieldInitialValue: { visible: boolean; width: number }
  futureEventsFieldInitialValue: { visible: boolean; width: number }
  contactStatusFieldInitialValue: { visible: boolean; width: number }
  updatedFieldInitialValue: { visible: boolean; width: number }
  phaseFieldInitialValue: { visible: boolean; width: number }
  activeShortlist?: Shortlist
  updateTableConfig: (value: ShortlistCategoriesConfigProps) => void
  onUpsertCustomColumnClick?: React.MouseEventHandler<HTMLButtonElement>
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  anchorEl: Nullable<HTMLElement>
  setAnchorEl: (value: Nullable<HTMLElement>) => void
}

export interface TableHeadCellProps {
  tableColumn: Header<ShortlistTableConfigProps, unknown>
  fieldName: string
  restCellProps?: { [key: string]: string | number | boolean }
  cellComponent: (() => ReactNode) | ((customField?: ShortListCustomField) => React.JSX.Element)
  columnTitle?: string
  isSortable?: boolean
  isSortActive: boolean
  sortOrder?: ShortlistSortOrder
  onSortChange: (newValue: ShortlistSort) => void
  updateConfig: (config: ShortlistCategoriesConfigProps) => void
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  customField?: ShortListCustomField
}

export interface ProfileTitleCellProps {
  profileData: ShortlistExtendedProfile
  isSelectedProfile: boolean
  onSelectProfile: (profileId: ShortlistExtendedProfile['profileId']) => void
  onClickProfile?: () => void
  isDisabled: boolean
  isPrivate?: boolean
  isDraggable?: boolean
  showCompanyName?: boolean
}

export interface ShortlistCategoriesConfigProps {
  title?: {
    visible: boolean
    width: number
  }
  nda?: {
    visible: boolean
    width: number
  }
  note?: {
    visible: boolean
    width: number
  }
  tagId?: {
    visible: boolean
    width: number
  }
  priority?: {
    visible: boolean
    width: number
  }
  createdAt?: {
    visible: boolean
    width: number
  }
  company?: {
    visible: boolean
    width: number
  }
  lastEvent?: {
    visible: boolean
    width: number
  }
  futureEvents?: {
    visible: boolean
    width: number
  }
  contactStatus?: {
    visible: boolean
    width: number
  }
  updatedAt?: {
    visible: boolean
    width: number
  }
  phase?: {
    visible: boolean
    width: number
  }
  [key: string]:
    | {
        visible: boolean
        width: number
      }
    | undefined
}

export enum ShortlistSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
  NOT_ASC_DESC = 'NOT_ASC_DESC',
}

export enum ShortlistProfilePriority {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  NOT_SET = 'NOT_SET',
}

export const DEFAULT_SHORTLIST_SORT: ShortlistSort = {
  fieldName: 'createdAt',
  sortOrder: ShortlistSortOrder.DESC,
}

export enum ShortlistSortProfileValue {
  ASSET_COMPANY_TITLE = 'ASSET_COMPANY_TITLE',
  DATE_ADDED_ASCENDING = 'DATE_ADDED_ASCENDING',
  DATE_ADDED_DESCENDING = 'DATE_ADDED_DESCENDING',
  NEWEST_EVENT = 'NEWEST_EVENT',
  OLDEST_EVENT = 'OLDEST_EVENT',
  STATUS_TYPE = 'STATUS_TYPE',
  DATE_UPDATED_ASCENDING = 'DATE_UPDATED_ASCENDING',
  DATE_UPDATED_DESCENDING = 'DATE_UPDATED_DESCENDING',
  DONE = 'DONE',
  NOT_DONE = 'NOT_DONE',
  NEWEST_NOTE = 'NEWEST_NOTE',
  OLDEST_NOTE = 'OLDEST_NOTE',
  PRIORITY_HIGH_TO_NOT_SET = 'PRIORITY_HIGH_TO_NOT_SET',
  PRIORITY_NOT_SET_TO_HIGH = 'PRIORITY_NOT_SET_TO_HIGH',
  STATUS = 'STATUS',
  COMPANY_NAME_ASC = 'COMPANY_NAME_ASC',
  COMPANY_NAME_DESC = 'COMPANY_NAME_DESC',
  MOST_RECENT_EVENT_FIRST = 'MOST_RECENT_EVENT_FIRST',
  NEXT_EVENT = 'NEXT_EVENT',
  NEWEST_TEXTS = 'NEWEST_TEXTS',
  OLDEST_TEXTS = 'OLDEST_TEXTS',
  FREQUENCY = 'FREQUENCY',
  PROFILE_TITLE_TYPE = 'PROFILE_TITLE_TYPE',
  PROFILE_TITLE_ASC = 'PROFILE_TITLE_ASC',
  PROFILE_TITLE_DESC = 'PROFILE_TITLE_DESC',
  ALERT_DATE_ADDED_ASCENDING = 'ALERT_DATE_ADDED_ASCENDING',
  ALERT_DATE_ADDED_DESCENDING = 'ALERT_DATE_ADDED_DESCENDING',
  ALERT_DATE_UPDATE_ASCENDING = 'ALERT_DATE_UPDATE_ASCENDING',
  ALERT_DATE_UPDATE_DESCENDING = 'ALERT_DATE_UPDATE_DESCENDING',
  ALERT_FREQUENCY = 'ALERT_FREQUENCY',
}

export interface Tag {
  id: number
  color: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface CompanyParticipant {
  firstName: string
  lastName: string
  id: number
  userId: number
}

export interface ShotlistDownload {
  status: ShotlistDownloadStatus
  url: string
}

export enum ShotlistDownloadStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export const MAX_CUSTOM_CATEGORY_LENGTH = 50

export enum FilterOperator {
  CONTAINS = 'CONTAINS',
  IS_EMPTY = 'IS_EMPTY',
  IS_NOT_EMPTY = 'IS_NOT_EMPTY',
  EQUALS = 'EQUALS',
  BETWEEN = 'BETWEEN',
}

export enum FilterCombinator {
  AND = 'AND',
  OR = 'OR',
}

export interface ApiFilterItem<T = string> {
  attribute: T | number
  operator: FilterOperator
  value?: string
  combinator?: FilterCombinator | string
  nestedAttribute?: string | number
}

export enum ShortlistFilterField {
  title = 'title',
  companyName = 'companyName',
  lastEvent = 'lastEvent',
  nda = 'nda',
  priority = 'priority',
  tagId = 'tagId',
  customFieldValues = 'customFieldValues',
  note = 'note',
  phase = 'phase',
  updatedAt = 'updatedAt',
  createdAt = 'createdAt',
}

export type ShortlistColumnFilter = ApiFilterItem<ShortlistFilterField> & { type: ShortlistFilterField }

export enum ShortlistFilterFieldType {
  TEXT = 'text',
  DATE_RANGE = 'date_range',
}
